/**
 * Line Items card: heading + tabs (Line Items / Freight / Miscellaneous Fees / Ignored).
 * Delegates each tab panel to a dedicated component to keep file size under 750 lines.
 */
import React, { useEffect, useMemo } from 'react';
import { Box, Flex, HStack, Icon, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip } from '@chakra-ui/react';
import { TbFlag } from 'react-icons/tb';
import type { InvoiceDetailResponse, InvoicePart, ReconciliationMatchNote } from '../../types/invoice';
import type { ZoomImageHighlightBox } from '../ZoomImage';
import { isCreditCardMiscFeeLine } from '../../utils/invoicePaySettlement';
import {
    invoiceFreightTabHasOrangeFlag,
    invoiceLineItemsTabHasOrangeFlag,
    invoiceMiscFeesTabHasOrangeFlag,
    invoiceReturnSlipIgnoredTabHasOrangeFlag,
    type InvoiceTabOrangeFlagContext,
} from '../../utils/invoiceLineItemTabOrangeFlags';
import { InvoiceLineItemsTab } from './InvoiceLineItemsTab';
import { InvoiceFreightTab } from './InvoiceFreightTab';
import { InvoiceMiscellaneousFeesTab } from './InvoiceMiscellaneousFeesTab';
import { InvoiceIgnoredPartsTab } from './InvoiceIgnoredPartsTab';

export type LineItemField = keyof InvoiceDetailResponse['lineItems'][0];

export type LineItemRowSnapshot = { row: number; item: InvoicePart };

export interface InvoiceLineItemsSectionProps {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    lineItemsTabIndex: number;
    setLineItemsTabIndex: (i: number) => void;
    lineItemsTotal: number;
    freightTotal: number;
    fuelItemsTotal: number;
    restockingFeeTotal: number;
    discountTotal: number;
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    unmatchedPartIndices: Set<number>;
    localMatchedNexOverrides: Record<number, any>;
    flaggedPartIndices: Set<number>;
    expandedMatchedNexRows: Set<string>;
    setExpandedMatchedNexRows: React.Dispatch<React.SetStateAction<Set<string>>>;
    expandedMatchedNexIgnoredRows: Set<number>;
    setExpandedMatchedNexIgnoredRows: React.Dispatch<React.SetStateAction<Set<number>>>;
    setUnmatchConfirmModal: (v: { partIndex: number; partNumber?: string; nexPartNumber?: string; nexLine?: any } | null) => void;
    aboveLineItemsSubtotal: number;
    buildTabData: (label: string, count: number, total: number, showTotal?: boolean) => { label: string; badge: string; count: number };
    updateLineItem: (index: number, field: LineItemField, value: any) => void;
    /** Batch-restore line rows after spreadsheet paste undo (optional where parent does not support it). */
    restoreLineItemRowsSnapshot?: (snapshots: ReadonlyArray<LineItemRowSnapshot>) => void;
    addLineItem: () => void;
    addLineItemAbove: (index: number) => void;
    addLineItemAt: (index: number) => void;
    duplicateLineItemAt: (index: number) => void;
    removeLineItem: (index: number) => void;
    reorderLineItems: (fromIndex: number, toIndex: number) => void;
    reorderFreightItems: (fromIndex: number, toIndex: number) => void;
    reorderRestockingFeeItems: (fromIndex: number, toIndex: number) => void;
    reorderIgnoredParts: (fromIndex: number, toIndex: number) => void;
    addFreightItem: () => void;
    addFreightItemAt: (index: number) => void;
    addFreightItemAbove: (index: number) => void;
    duplicateFreightItemAt: (index: number) => void;
    removeFreightItem: (index: number) => void;
    updateFreightItem: (index: number, field: LineItemField, value: any) => void;
    addFuelItem: () => void;
    addFuelItemAt: (index: number) => void;
    removeFuelItem: (index: number) => void;
    updateFuelItem: (index: number, field: LineItemField, value: any) => void;
    addRestockingFeeItem: () => void;
    addRestockingFeeItemAt: (index: number) => void;
    addRestockingFeeItemAbove: (index: number) => void;
    duplicateRestockingFeeItemAt: (index: number) => void;
    removeRestockingFeeItem: (index: number) => void;
    updateRestockingFeeItem: (index: number, field: LineItemField, value: any) => void;
    restoreIgnoredPart: (index: number) => void;
    openRevertModal: (fieldKey: string, fieldLabel: string) => void;
    openReconciliationDetailModal: (modal: any) => void;
    getLineItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    /** Used for line-item grid Tab/Enter navigation into price cells. */
    setEditingNumericField?: (v: string | null) => void;
    /** Seeded string while editing price cells (type-to-replace from grid landing). */
    setEditingNumericValues?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    isLineItemFieldEdited: (index: number, field: LineItemField) => boolean;
    hasAnyLineItemFieldEdited: (index: number) => boolean;
    getLineItemEditedFieldLabels: (index: number) => string[];
    getIssuesForPart: (index: number) => any[];
    getLineItemExpandKey: (index: number, item: InvoiceDetailResponse['lineItems'][0]) => string;
    getIssueTooltip: (issues: any[], hasLocalMatch?: boolean) => React.ReactNode;
    getPartNumberChoiceIssue: (partIndex: number) => any;
    getMissingInNexSuggestion: (partIndex: number) => string | undefined;
    getFreightItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getFuelItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getRestockingFeeItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    renderLineItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderFreightItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderFuelItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderRestockingFeeItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    fmtUsd: (n: number) => string;
    addIgnoredPart: () => void;
    addIgnoredPartAt: (index: number) => void;
    addIgnoredPartAbove: (index: number) => void;
    removeIgnoredPart: (index: number) => void;
    updateIgnoredPart: (index: number, field: LineItemField, value: any) => void;
    getIgnoredPartStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    renderIgnoredPartNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    effectiveMatchedNexLines?: Array<{ partIndex: number; nexLine: any }>;
    effectiveReconciliationMatchNotes?: ReconciliationMatchNote[];
    /** When true, all line item inputs are view-only. */
    isReadOnly: boolean;
    /** When true, show Freight tab even when there are no freight rows. */
    alwaysShowFreightTab?: boolean;
    /** When true, omit the section heading (parent provides foldable title). */
    hideTitle?: boolean;
    /** After Match from NEX: hide orange "Value changed" revert icons on line item fields (vs original extraction). */
    suppressLineItemValueChangedRevertUi?: boolean;
    onLineItemImageHighlightChange?: (region: ZoomImageHighlightBox | null) => void;
    /** Show per-cell table gridlines in line-item tabs. */
    showTableGridlines?: boolean;
    /** Org setting: which NEX qty field to display for received/sublet. */
    nexLineQuantityBasis?: import('../../utils/nexLineQuantity').NexLineQuantityBasis;
}

export function InvoiceLineItemsSection(props: InvoiceLineItemsSectionProps) {
    const {
        invoice,
        editedFields,
        lineItemsTabIndex,
        setLineItemsTabIndex,
        lineItemsTotal,
        freightTotal,
        restockingFeeTotal,
        buildTabData,
        aboveLineItemsSubtotal,
        alwaysShowFreightTab,
        hideTitle = false,
        flaggedPartIndices,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        lineItemBackendIndexByCurrentIndex,
        getIssuesForPart,
        getLineItemStringValue,
        effectiveMatchedNexLines,
        effectiveReconciliationMatchNotes,
    } = props;

    const ignoredCount = (editedFields.ignoredParts ?? invoice?.ignoredParts ?? (invoice as { canceledParts?: InvoicePart[] })?.canceledParts ?? []).length;
    const isReturnSlip = invoice?.invoiceType === 'return_slip';
    const lineCount = (editedFields.lineItems || invoice?.lineItems || []).length;
    const freightCount = (editedFields.freightItems ?? invoice?.freightItems ?? []).length;
    const restockingRaw = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const payCharge = (editedFields.paySettlementType ?? invoice?.paySettlementType) === 'charge';
    const restockingCount = payCharge
        ? restockingRaw.filter((p) => !isCreditCardMiscFeeLine(p)).length
        : restockingRaw.length;

    const tabDefs = useMemo(() => {
        const defs: Array<{
            key: string;
            tab: { label: string; badge: string; count: number };
            panel: React.ReactNode;
        }> = [];

        if (isReturnSlip) {
            defs.push({
                key: 'returned-items',
                tab: { label: 'Returned Items', badge: `${ignoredCount}`, count: ignoredCount },
                panel: <InvoiceIgnoredPartsTab {...props} aboveLineItemsSubtotal={aboveLineItemsSubtotal} />,
            });
            return defs;
        }

        defs.push({
            key: 'line',
            tab: buildTabData('Line Items', lineCount, lineItemsTotal),
            panel: <InvoiceLineItemsTab {...props} aboveLineItemsSubtotal={aboveLineItemsSubtotal} />,
        });

        if (freightCount > 0 || alwaysShowFreightTab) {
            defs.push({
                key: 'freight',
                tab: buildTabData('Freight', freightCount, freightTotal),
                panel: <InvoiceFreightTab {...props} aboveLineItemsSubtotal={aboveLineItemsSubtotal} />,
            });
        }

        defs.push({
            key: 'miscellaneous',
            tab: buildTabData('Miscellaneous Fees', restockingCount, restockingFeeTotal),
            panel: <InvoiceMiscellaneousFeesTab {...props} aboveLineItemsSubtotal={aboveLineItemsSubtotal} />,
        });

        defs.push({
            key: 'ignored',
            tab: { label: 'Ignored', badge: `${ignoredCount}`, count: ignoredCount },
            panel: <InvoiceIgnoredPartsTab {...props} aboveLineItemsSubtotal={aboveLineItemsSubtotal} />,
        });

        return defs;
    }, [
        lineCount,
        freightCount,
        restockingCount,
        ignoredCount,
        lineItemsTotal,
        freightTotal,
        restockingFeeTotal,
        isReturnSlip,
        buildTabData,
        aboveLineItemsSubtotal,
        alwaysShowFreightTab,
        props,
    ]);

    const tabOrangeFlagByKey = useMemo(() => {
        const ctx: InvoiceTabOrangeFlagContext = {
            invoice,
            editedFields,
            lineItemBackendIndexByCurrentIndex,
            unmatchedPartIndices,
            localMatchedNexOverrides,
            flaggedPartIndices,
            effectiveMatchedNexLines,
            effectiveReconciliationMatchNotes,
            getIssuesForPart,
            getLineItemStringValue,
        };
        if (isReturnSlip) {
            return { 'returned-items': invoiceReturnSlipIgnoredTabHasOrangeFlag(ctx) } as Record<string, boolean>;
        }
        const out: Record<string, boolean> = {
            line: invoiceLineItemsTabHasOrangeFlag(ctx),
            miscellaneous: invoiceMiscFeesTabHasOrangeFlag(ctx),
            ignored: false,
        };
        if (freightCount > 0 || alwaysShowFreightTab) {
            out.freight = invoiceFreightTabHasOrangeFlag(ctx);
        }
        return out;
    }, [
        isReturnSlip,
        invoice,
        editedFields,
        lineItemBackendIndexByCurrentIndex,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        effectiveMatchedNexLines,
        effectiveReconciliationMatchNotes,
        getIssuesForPart,
        getLineItemStringValue,
        freightCount,
        alwaysShowFreightTab,
    ]);

    useEffect(() => {
        if (lineItemsTabIndex >= tabDefs.length) setLineItemsTabIndex(0);
    }, [lineItemsTabIndex, tabDefs.length, setLineItemsTabIndex]);

    return (
        <Box>
            {!hideTitle && (
                <Text color="gray.900" fontSize="sm" fontWeight="bold" mb={3}>
                    Line Items
                </Text>
            )}
            <Box
                bg="white"
                borderRadius={hideTitle ? '0' : 'lg'}
                border={hideTitle ? 'none' : '1px solid'}
                borderColor="gray.200"
                p={hideTitle ? 0 : 4}
                overflow="visible"
            >
                <Tabs
                    variant="unstyled"
                    index={lineItemsTabIndex}
                    onChange={setLineItemsTabIndex}
                    isFitted
                    isLazy
                    overflow="visible"
                >
                    <TabList border="none" gap={0} borderBottom="1.5px solid" borderColor="gray.200" mb={4}>
                        {tabDefs.map((t, idx) => {
                            const tabHasFlag = !!tabOrangeFlagByKey[t.key];
                            const isSelected = idx === lineItemsTabIndex;
                            return (
                            <Tab
                                key={idx}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.500"
                                bg={tabHasFlag && !isSelected ? 'orange.50' : 'transparent'}
                                border="none"
                                borderBottom="1.5px solid"
                                borderBottomColor={tabHasFlag && !isSelected ? 'gray.300' : 'transparent'}
                                borderRadius={0}
                                mb="-1.5px"
                                py={2}
                                px={3}
                                _selected={
                                    tabHasFlag
                                        ? { color: 'gray.800', bg: 'orange.100', border: '1.5px solid', borderColor: 'orange.400', borderBottomColor: 'orange.400', borderRadius: 0 }
                                        : { color: 'gray.800', borderBottom: '1.5px solid', borderBottomColor: 'gray.500' }
                                }
                                _hover={{
                                    color: 'gray.700',
                                    ...(tabHasFlag ? { bg: 'orange.100' } : {}),
                                }}
                            >
                                <HStack spacing={1} align="center">
                                    {tabHasFlag ? (
                                        <Tooltip label="This tab has flagged line items" placement="top" hasArrow>
                                            <Box as="span" display="inline-flex" flexShrink={0} lineHeight={0} aria-label="This tab has flagged line items">
                                                <Icon as={TbFlag as React.ElementType} boxSize={3.5} color="orange.500" />
                                            </Box>
                                        </Tooltip>
                                    ) : null}
                                    <Text>{t.tab.label}</Text>
                                    <Flex ml={1.5} bg="gray.100" borderRadius="lg" h="18px" minW="18px" px="5px" align="center" justify="center" flexShrink={0}>
                                        <Text fontSize="11px" fontWeight="bold" lineHeight="1" color="gray.800">{t.tab.badge}</Text>
                                    </Flex>
                                </HStack>
                            </Tab>
                            );
                        })}
                    </TabList>
                    <TabPanels overflow="visible">
                        {tabDefs.map((t) => (
                            <TabPanel key={t.key} p={0} overflow="visible">
                                {t.panel}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
}
